import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { shipmentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { 
  Ship, LogOut, Plus, LayoutDashboard, Package, Users,
  Search, Filter, ChevronRight, Loader2, Plane, Truck,
  ArrowLeft, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { getStatusColor, getStatusLabel, formatDate } from '../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

function Sidebar({ onLogout }) {
  return (
    <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 p-6">
      <Link to="/dashboard" className="flex items-center gap-2 mb-8">
        <div className="w-8 h-8 bg-importdocs-primary rounded-md flex items-center justify-center">
          <Ship className="w-5 h-5 text-white" />
        </div>
        <span className="font-heading font-bold text-xl text-importdocs-text">ImportDocs</span>
      </Link>
      
      <nav className="flex-1 space-y-1">
        <Link
          to="/dashboard"
          className="flex items-center gap-3 px-4 py-3 rounded-md text-importdocs-text-secondary hover:bg-slate-100"
        >
          <LayoutDashboard className="w-5 h-5" />
          Dashboard
        </Link>
        <Link
          to="/dashboard/shipments"
          className="flex items-center gap-3 px-4 py-3 rounded-md bg-importdocs-primary text-white"
        >
          <Package className="w-5 h-5" />
          Shipments
        </Link>
        <Link
          to="/dashboard/suppliers"
          className="flex items-center gap-3 px-4 py-3 rounded-md text-importdocs-text-secondary hover:bg-slate-100"
        >
          <Users className="w-5 h-5" />
          Suppliers
        </Link>
      </nav>
      
      <Button 
        variant="ghost" 
        className="justify-start text-importdocs-text-secondary hover:text-importdocs-danger"
        onClick={onLogout}
      >
        <LogOut className="w-5 h-5 mr-2" />
        Logout
      </Button>
    </aside>
  );
}

function ShipmentRow({ shipment, onDelete }) {
  const TransportIcon = shipment.transport_type === 'Sea' ? Ship : 
                        shipment.transport_type === 'Air' ? Plane : Truck;
  
  return (
    <div className="flex items-center gap-4 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`shipment-row-${shipment.id}`}>
      <div className="w-10 h-10 rounded-lg bg-importdocs-subtle flex items-center justify-center flex-shrink-0">
        <TransportIcon className="w-5 h-5 text-importdocs-primary" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-importdocs-text truncate">{shipment.name}</h3>
          <Badge className={`${getStatusColor(shipment.status)} border text-xs`}>
            {getStatusLabel(shipment.status)}
          </Badge>
        </div>
        <div className="flex items-center gap-4 text-sm text-importdocs-text-secondary mt-1">
          <span className="font-mono">{shipment.reference}</span>
          <span>{shipment.supplier_name}</span>
          <span>{shipment.country_of_origin}</span>
        </div>
      </div>
      
      <div className="hidden md:flex items-center gap-8">
        <div className="text-right">
          <p className="text-xs text-importdocs-text-muted">ETA</p>
          <p className="text-sm text-importdocs-text">{formatDate(shipment.estimated_arrival)}</p>
        </div>
        <div className="w-24">
          <p className="text-xs text-importdocs-text-muted mb-1">Docs {shipment.document_completion}%</p>
          <Progress value={shipment.document_completion} className="h-2" />
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <Link to={`/dashboard/shipments/${shipment.id}`}>
          <Button variant="ghost" size="sm" data-testid={`view-btn-${shipment.id}`}>
            View
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
        <Button 
          variant="ghost" 
          size="icon" 
          className="text-importdocs-text-muted hover:text-importdocs-danger"
          onClick={() => onDelete(shipment)}
          data-testid={`delete-btn-${shipment.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function ShipmentsListPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadShipments();
  }, []);

  const loadShipments = async () => {
    try {
      const res = await shipmentsAPI.getAll();
      setShipments(res.data);
    } catch (error) {
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await shipmentsAPI.delete(deleteTarget.id);
      setShipments(prev => prev.filter(s => s.id !== deleteTarget.id));
      toast.success('Shipment deleted');
    } catch (error) {
      toast.error('Failed to delete shipment');
    } finally {
      setDeleteTarget(null);
    }
  };

  const filteredShipments = shipments.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         s.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         s.reference.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-importdocs-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-importdocs-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-importdocs-bg flex">
      <Sidebar onLogout={handleLogout} />
      
      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <h1 className="font-heading font-bold text-xl text-importdocs-text">All Shipments</h1>
            </div>
            <Link to="/dashboard/shipments/new">
              <Button className="bg-importdocs-primary hover:bg-importdocs-primary-dark" data-testid="new-shipment-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Shipment
              </Button>
            </Link>
          </div>
        </header>
        
        <main className="flex-1 p-6">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-importdocs-text-muted" />
                  <Input 
                    placeholder="Search by name, supplier, or reference..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-slate-200"
                    data-testid="search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48 border-slate-200" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="documents_uploaded">Docs Uploaded</SelectItem>
                    <SelectItem value="cleared">Cleared</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {filteredShipments.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-importdocs-text-muted mx-auto mb-4" />
                  <p className="text-importdocs-text-secondary">
                    {searchTerm || statusFilter !== 'all' 
                      ? 'No shipments match your filters' 
                      : 'No shipments yet'}
                  </p>
                  {!searchTerm && statusFilter === 'all' && (
                    <Link to="/dashboard/shipments/new">
                      <Button variant="outline" className="mt-4">
                        Create your first shipment
                      </Button>
                    </Link>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredShipments.map((shipment) => (
                    <ShipmentRow 
                      key={shipment.id} 
                      shipment={shipment} 
                      onDelete={setDeleteTarget}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
      
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shipment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-importdocs-danger hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
